// ============================================================================
// pushNotifications — helpers de inscrição em Web Push
// ============================================================================
// Fluxo de opt-in:
//   1. Verifica suporte (PushManager + Notification + ServiceWorker)
//   2. Pede permissão (Notification.requestPermission)
//   3. Pega VAPID pubkey do servidor (cache em memória)
//   4. Subscribe no pushManager
//   5. Manda subscription pro /api/push-subscribe
// ============================================================================

const SUBSCRIBED_FLAG_KEY = 'fk_push_subscribed_for'; // valor = professionalId

export function isPushSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export function getPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

// Converte base64url → Uint8Array (formato exigido pelo applicationServerKey)
function urlB64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

let _vapidKeyCache = null;
async function getVapidPublicKey() {
  if (_vapidKeyCache) return _vapidKeyCache;
  const r = await fetch('/api/push-vapid-key');
  if (!r.ok) throw new Error('Falha ao obter VAPID key');
  const { publicKey } = await r.json();
  _vapidKeyCache = publicKey;
  return publicKey;
}

async function getRegistration() {
  const reg = await navigator.serviceWorker.ready;
  return reg;
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  try {
    const reg = await getRegistration();
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export async function isSubscribed() {
  const sub = await getCurrentSubscription();
  return !!sub;
}

// 30/10/2026 (aviso de mensagem nova no computador da recepção, crmAvisoComputador.js): o destino
// opcional das duas pontas. { destino: 'crm-computador' } vai no corpo e o servidor grava a linha como
// 'crm-pc:<id da SESSÃO>' (o id nunca vem do corpo), separada do push pessoal: o sendPush de sempre nem
// enxerga essa linha e o aviso de mensagem só vai para ela. Destino vazio, null ou não string = sem destino.
function destinoDe(opcoes) {
  const d = opcoes && typeof opcoes === 'object' ? opcoes.destino : null;
  return typeof d === 'string' && d ? d : null;
}

// Inscreve o device + manda pro servidor associando ao professionalId
// recipientId = professionalId OU id do usuário (admin master sem perfil).
// Só é usado como guarda "tem sessão utilizável"; o servidor deduz o
// destinatário real da sessão (x-session-token), não deste parâmetro.
// 30/10/2026: opcoes.destino (acima). Sem opções, exatamente como antes (o NotificationsGate e o
// NotificationsToggle chamam assim); com destino, a marca SUBSCRIBED_FLAG_KEY (do push pessoal) não é gravada.
export async function subscribeToPush(recipientId, opcoes = {}) {
  const destino = destinoDe(opcoes);
  if (!isPushSupported()) throw new Error('Push não suportado neste dispositivo');
  if (!recipientId) throw new Error('Sessão sem identificador');

  // 1) Permissão
  let perm = Notification.permission;
  if (perm === 'default') {
    perm = await Notification.requestPermission();
  }
  if (perm !== 'granted') {
    throw new Error('Permissão de notificação negada');
  }

  // 2) Subscribe
  const reg = await getRegistration();
  const existing = await reg.pushManager.getSubscription();
  let sub = existing;
  if (!sub) {
    const pubKey = await getVapidPublicKey();
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(pubKey),
    });
  }

  // 3) Persiste no servidor (header x-session-token autentica a request)
  const sessionToken = getSessionToken();
  if (!sessionToken) throw new Error('Sessão inválida — faça login de novo');
  const r = await fetch('/api/push-subscribe', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-session-token': sessionToken,
    },
    body: JSON.stringify({
      // professionalId é deduzido server-side a partir da sessão — não mandamos do client
      subscription: sub.toJSON(),
      userAgent: navigator.userAgent,
      ...(destino ? { destino } : {}),
    }),
  });
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`Falha ao registrar no servidor: ${text}`);
  }

  // 30/10/2026: era professionalId (não existe aqui): o ReferenceError caía no catch e a marca nunca era gravada
  if (!destino) { try { localStorage.setItem(SUBSCRIBED_FLAG_KEY, recipientId); } catch {} }
  return sub;
}

// Lê sessionToken do sessionStorage (set pelo AuthContext no login)
function getSessionToken() {
  try {
    const stored = sessionStorage.getItem('fk_session');
    if (!stored) return null;
    return JSON.parse(stored)?.sessionToken || null;
  } catch { return null; }
}

// 30/10/2026: opcoes.destino (acima). Sem opções, exatamente como antes. Com destino, o servidor tira SÓ a
// linha 'crm-pc:<id da sessão>' deste endpoint e a inscrição do navegador FICA: é a mesma do push pessoal
// (o app instalado no computador refaz o vínculo pessoal no NotificationsGate). Ela só sai daqui quando o
// servidor não confirmou (sem sessão, rede caída, erro): a chave desligada não pode deixar o aviso do CRM
// chegando. A marca do push pessoal só sai junto com a inscrição do navegador.
export async function unsubscribeFromPush(opcoes = {}) {
  const destino = destinoDe(opcoes);
  if (!isPushSupported()) return false;
  const reg = await getRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) {
    if (!destino) { try { localStorage.removeItem(SUBSCRIBED_FLAG_KEY); } catch {} }
    return true;
  }
  // Remove no servidor antes de unsubscribe local — se cair internet, fica no SW
  // mas o servidor já não tenta enviar (e cleanup acontece no próximo send).
  const sessionToken = getSessionToken();
  let saiuNoServidor = false;
  if (sessionToken) {
    try {
      const r = await fetch('/api/push-unsubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-token': sessionToken,
        },
        body: JSON.stringify({ endpoint: sub.endpoint, ...(destino ? { destino } : {}) }),
        keepalive: true,
      });
      saiuNoServidor = !!(r && r.ok);
    } catch {}
  }
  if (destino && saiuNoServidor) return true;
  await sub.unsubscribe();
  try { localStorage.removeItem(SUBSCRIBED_FLAG_KEY); } catch {}
  return true;
}

// Listener pra re-subscrever quando o SW dispara PUSH_SUBSCRIPTION_CHANGED
export function setupSubscriptionChangeListener(getProfessionalId) {
  if (!isPushSupported()) return () => {};
  const handler = (event) => {
    if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED') {
      const profId = getProfessionalId();
      if (profId) subscribeToPush(profId).catch(() => {});
    }
  };
  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}
